import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { LocationForecastResponse } from './interfaces';
import { weatherDataMock } from './weather-data-mock';
import { environment } from '@env/environment';

interface CacheEntry {
  data: LocationForecastResponse;
  expiry: number;
}

@Injectable({ providedIn: 'root' })
export class WeatherService {
  private http = inject(HttpClient);

  private readonly apiUrl = 'https://api.met.no/weatherapi/locationforecast/2.0/compact';
  private readonly cachingTime = 60 * 60 * 1000; // 1 hour

  private cache = new Map<number, CacheEntry>();
  private inCache = new Map<number, Observable<LocationForecastResponse>>();

  fetchWeather(
    routeNetworkId: number,
    lat: number,
    lon: number,
  ): Observable<LocationForecastResponse | null> {
    if (!environment.production) {
      return of(weatherDataMock);
    }

    const now = Date.now();
    const cached = this.cache.get(routeNetworkId);

    if (cached && cached.expiry > now) {
      return of(cached.data);
    }

    const existingRequest = this.inCache.get(routeNetworkId);
    if (existingRequest) {
      return existingRequest;
    }

    const request$ = this.http
      .get<LocationForecastResponse>(`${this.apiUrl}?lat=${lat}&lon=${lon}`)
      .pipe(
        tap((response) => {
          this.cache.set(routeNetworkId, {
            data: response,
            expiry: Date.now() + this.cachingTime,
          });
          this.inCache.delete(routeNetworkId);
        }),
        shareReplay(1),
      );

    this.inCache.set(routeNetworkId, request$);

    return request$;
  }
}
